/**
 * Detección simple de intención en la respuesta de un prospecto y sugerencia de
 * qué contestar. Es por reglas (sin IA externa): rápido, gratis y sin promesas
 * de ingresos. La persona siempre lee, ajusta y envía el mensaje ella misma.
 */

export type ReplyIntent =
  | "interested"
  | "what_is"
  | "price"
  | "link"
  | "trust"
  | "later"
  | "not_interested"
  | "other";

export const INTENT_LABELS: Record<ReplyIntent, string> = {
  interested: "Le interesa",
  what_is: "Pide información",
  price: "Pregunta el costo",
  link: "Pide el link",
  trust: "Duda / desconfianza",
  later: "Más tarde",
  not_interested: "No le interesa",
  other: "Otra respuesta",
};

export const INTENT_ORDER: ReplyIntent[] = [
  "interested",
  "what_is",
  "price",
  "link",
  "trust",
  "later",
  "not_interested",
  "other",
];

const norm = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s?]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const has = (t: string, words: string[]) => words.some((w) => t.includes(w));

export function detectIntent(raw: string): ReplyIntent {
  const t = ` ${norm(raw)} `;
  if (!t.trim()) return "other";
  if (has(t, [" no me interesa", " no gracias", " no estoy interesad", " no quiero", " no me escribas", " no molest", " deja de escribir", " no por favor"])) {
    return "not_interested";
  }
  if (has(t, ["piramide", "estafa", "multinivel", " mlm", "network", "es seguro", "es real", "fraude", "enga", "de que empresa", "quien eres", "quien es usted", "es confiable"])) {
    return "trust";
  }
  if (has(t, ["precio", "costo", "cuanto cuesta", "cuanto vale", "cuanto es", "cuanto pago", "tiene costo", "es gratis", "es gratuito", "cuanto debo", "inversion"])) {
    return "price";
  }
  if (has(t, [" link", " enlace", "pasame", "mandame", "enviame", "donde me registro", "donde entro", "como me registro"])) {
    return "link";
  }
  if (has(t, [" mas tarde", "luego", "despues", "ahora no", "otro momento", "estoy ocupad", "en otro rato", "manana"])) {
    return "later";
  }
  if (has(t, ["que es", "de que se trata", "como funciona", "en que consiste", "cuentame", "cuenta mas", "explica", "informacion", "mas info", "detalles", "dime mas", "cuentame mas"])) {
    return "what_is";
  }
  if (/(^| )(si|claro|dale|ok|okay|listo|perfecto|me interesa|quiero|me gustaria|cuando|adelante|por supuesto|buena idea)( |$)/.test(t)) {
    return "interested";
  }
  return "other";
}

export type ReplySuggestion = {
  intent: ReplyIntent;
  /** Texto con {nombre} y {link} ya resueltos. */
  text: string;
  /** true si el texto incluye el link único. */
  includesLink: boolean;
  /** Etapa del embudo a la que conviene mover al prospecto. */
  stage: string | null;
  /** Qué hacer a continuación (se guarda como "próxima acción"). */
  nextAction: string;
};

export function buildSuggestion(intent: ReplyIntent, firstName: string, link: string): ReplySuggestion {
  const name = firstName ? ` ${firstName}` : "";
  const hi = `¡Hola${name}!`;
  switch (intent) {
    case "interested":
      return {
        intent,
        includesLink: true,
        stage: "Presentación",
        nextAction: "Esperar si abre el link",
        text: `${hi} Qué bueno 😊 Te dejo aquí el enlace del webinar gratuito, dura poco y ahí te explico todo con calma:\n\n${link}\n\nCuando lo veas, cuéntame qué te pareció y resolvemos tus dudas por aquí.`,
      };
    case "what_is":
      return {
        intent,
        includesLink: false,
        stage: "Respondió",
        nextAction: "Responderle y ofrecer el webinar",
        text: `${hi} Con gusto. En VÍVELO AI ayudamos a creadores y emprendedores a organizar sus hábitos, su prospección y su seguimiento con un sistema sencillo. Lo explico en un webinar gratuito y corto, sin compromiso.\n\n¿Te paso el enlace para que lo veas cuando te quede cómodo?`,
      };
    case "price":
      return {
        intent,
        includesLink: false,
        stage: "Respondió",
        nextAction: "Responderle y ofrecer el webinar",
        text: `${hi} Buena pregunta. El webinar donde te explico todo es gratuito y no tiene compromiso. Ahí ves cómo funciona y decides con calma si es para ti.\n\n¿Te paso el enlace?`,
      };
    case "link":
      return {
        intent,
        includesLink: true,
        stage: "Presentación",
        nextAction: "Esperar si abre el link",
        text: `${hi} Claro, aquí está el enlace al webinar gratuito:\n\n${link}\n\nCuando lo veas me cuentas qué te pareció y respondo lo que quieras.`,
      };
    case "trust":
      return {
        intent,
        includesLink: true,
        stage: "Respondió",
        nextAction: "Responderle con transparencia",
        text: `${hi} Qué bueno que lo preguntas, es sano preguntar. No te pido dinero por este chat ni te prometo resultados ni ingresos: lo que hago es invitarte a un webinar gratuito donde lo ves todo tú misma y decides si te sirve o no.\n\nSi quieres verlo: ${link}\n\nY cualquier duda me la escribes con confianza.`,
      };
    case "later":
      return {
        intent,
        includesLink: false,
        stage: "Seguimiento",
        nextAction: "Volver a escribirle en unos días",
        text: `${hi} Sin problema, tómate tu tiempo 🙌 Te escribo en unos días para ver cómo vas. Si antes te surge alguna duda, me avisas por aquí.`,
      };
    case "not_interested":
      return {
        intent,
        includesLink: false,
        stage: null,
        nextAction: "Cerrar con cortesía (no insistir)",
        text: `${hi} Gracias por responderme y por tu sinceridad. Te deseo lo mejor con tu contenido y tus proyectos 🙌`,
      };
    default:
      return {
        intent: "other",
        includesLink: false,
        stage: "Respondió",
        nextAction: "Responderle",
        text: `${hi} Gracias por escribirme 😊 Cuéntame un poco más sobre lo que buscas ahora mismo y vemos si te puedo ayudar.`,
      };
  }
}